import {
  useCrateTourPresentationMutation,
  useCreateTourPresentationTokenMutation,
} from '@carboncopyco/data';
import {
  DownOutlineIcon,
  EditIcon,
  PlayIcon,
  ShareOutlineIcon,
  ShowOutlineIcon,
} from '@carboncopyco/icons';
import { PermissionAction, PermissionKey } from '@carboncopyco/types';
import { Button, Separator } from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import NiceModal from '@ebay/nice-modal-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { TourPageActionsDropdown } from '../../AssetActions/Tour';
import { CreateShareTourFlow } from '../../Share/ShareTourFlow';
import { usePermissionsContext } from '../../utils';
import { useTourPageProps, useTourShares } from './TourPageContext';

export const TourPageActionButtons = () => {
  const {
    organizationId,
    tourId,
    workspaceId,
    goToTour,
    goToTourPreview,
    handleEdit,
    goToCopiedTour,
    goToLinksTab,
    goToWorkspacePage,
    toursShareAspectRatioEnabled,
    goToTourPresentation,
    goToFolderPage,
    readOnly,
  } = useTourPageProps();
  const { addItem } = useTourShares();
  const flags = useFlags();
  const { trackEvent } = useAnalytics();
  const { getPermissions } = usePermissionsContext();
  const [createTourPresentation] = useCrateTourPresentationMutation();
  const [createTourPresentationToken] =
    useCreateTourPresentationTokenMutation();
  const updateTourPermission = getPermissions(
    PermissionKey.tour,
    PermissionAction.updateTourById
  );
  const location = 'tour_info_page_top_actions';

  const onEdit = () => {
    trackEvent(AnalyticsTrackEventName.TOUR_CLICK_EDIT, {
      location,
    });
    handleEdit();
  };

  const createAndNavigateToTourPresentation = async () => {
    const { id: tourPresentationId } = await createTourPresentation({
      organizationId,
      tourId: String(tourId),
      forClient: 'Showtime',
    }).unwrap();
    const { token } = await createTourPresentationToken({
      organizationId,
      tourPresentationId,
    }).unwrap();
    if (token && goToTourPresentation) {
      goToTourPresentation(token);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3">
        {!readOnly ? (
          <>
            <TourPageActionsDropdown
              goToShares={goToLinksTab}
              organizationId={organizationId}
              tourId={tourId}
              workspaceId={workspaceId}
              goToTour={goToTour}
              goToTourPreview={goToTourPreview}
              handleEdit={onEdit}
              goToCopiedTour={goToCopiedTour}
              goToWorkspacePage={goToWorkspacePage}
              goToFolderPage={goToFolderPage}
              asChild
              arrow
              align="center"
              triggerElement={
                <Button
                  link
                  blue
                  rightIcon={<DownOutlineIcon />}
                  label="Actions"
                />
              }
            />
            <div className="h-7 pr-2">
              <Separator />
            </div>
          </>
        ) : (
          <Button
            onClick={() => {
              trackEvent(AnalyticsTrackEventName.TOUR_CLICK_PREVIEW, {
                location,
              });
              goToTourPreview(tourId);
            }}
            secondary
            leftIcon={<ShowOutlineIcon size={18} />}
            label="Preview"
          />
        )}
        {!readOnly && updateTourPermission && (
          <Button
            onClick={handleEdit}
            secondary
            leftIcon={<EditIcon />}
            label="Edit"
          />
        )}

        <Button
          onClick={() => {
            trackEvent(AnalyticsTrackEventName.TOUR_CLICK_SHARE, {
              location,
            });
            NiceModal.show(CreateShareTourFlow, {
              workspaceId,
              toursShareAspectRatioEnabled: !!toursShareAspectRatioEnabled,
              organizationId,
              tourId,
              goToShares: goToLinksTab,
              onShareSuccess: addItem,
            });
          }}
          leftIcon={<ShareOutlineIcon />}
          label="Share tour"
        />
        {flags.tourPresentation && (
          <Button
            onClick={createAndNavigateToTourPresentation}
            label="Present tour"
            leftIcon={<PlayIcon />}
          />
        )}
      </div>
    </>
  );
};
